import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  QueryList,
  ViewChild,
  ViewChildren,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil, fromEvent } from 'rxjs';
import { ProfilePhotoService } from '../../../helpers/profile-photo.service';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { CommonService } from '../../../api/services/common.service';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { ToastrService } from 'ngx-toastr';
import { ButtonsComponent } from '../buttons/buttons.component';
import { PlaceholderWithTextComponent } from '../placeholder-with-text/placeholder-with-text.component';
import { InitialsService } from '../../../helpers/initial.service';
import { FORM_ID } from '../../../helpers/form-id.enum';
import { getAvatarBgColor } from '../../../helpers/avatar.helper';
import { NoDataViewComponent } from '../no-data-view/no-data-view.component';
import { Router } from '@angular/router';
import { InputLabelComponent } from '../input-label/input-label.component';
import { DropdownWithSearchComponent } from '../dropdown-with-search/dropdown-with-search.component';
import { OpenModalComponent } from '../open-modal/open-modal.component';
import { ModalRendererComponent } from '../../widgets/modal-renderer/modal-renderer.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    PlaceholderWithTextComponent,
    NoDataViewComponent,
    OpenModalComponent,
    ModalRendererComponent,
    OverlayModule,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.css',
})
export class TableComponent implements OnInit, OnDestroy {
  @Input() tableResponse: any;
  @Input() columns: any;
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() isWriteAccess: boolean = true;
  currentPage: number = 1;
  totalPages: number = 1;
  totalRecords: number = 0;
  pageNumbers: any[] = [];
  hasNameEmailMerge: boolean = false;
  fieldsData: any;
  currentLimit: any = 10;
  endpoint = environment.api_endpoint;
  columnsData: any;
  currentSort: { [key: string]: 'asc' | 'desc' } = {};
  activeModalContext: any;
  openDropdownId: string | null = null;
  isLoading: boolean = false;
  totalCount: any;
  searchInput: any;
  openAboveId: string | null = null;
  modalText: any;
  modalSubText: any;
  @ViewChild('tableBodyContainer') tableBodyContainer?: ElementRef<HTMLElement>;
  @ViewChildren('dropdownButton') dropdownButtons!: QueryList<ElementRef>;
  @ViewChildren('dropdownMenu') dropdownMenus!: QueryList<ElementRef>;
  buttonName: any;
  isLoader: boolean = true;
  defaultSkeletonCols: number[] = [1, 2, 3, 4];
  nodeId: any;
  saveForm!: FormGroup;
  formFields: any;
  visibleFields: { [fieldName: string]: boolean } = {};
  formData: any;
  footerFields: any;
  sortValue: any;
  isReloading: boolean = false;
  private searchTimeout: any;
  private activeLoadSubscription: any;
  addModalContext: any;
  currentOpenModalId: any;
  profilePhotoUrls: { [email: string]: string } = {};
  isFilterDropdownOpen: boolean = false;
  disabledBtn: boolean = false;
  dropdownPositions = [
    {
      originX: 'end' as const,
      originY: 'bottom' as const,
      overlayX: 'end' as const,
      overlayY: 'top' as const,
    },
  ];
  isFilterApply: boolean = true;
  labelAbove: boolean = false;
  filterOptions: any = {};
  hasActiveDropdownFilters: boolean = false;
  useStatusFallback: boolean = false;
  @ViewChild('modalRendererRef') modalRendererRef?: ModalRendererComponent;

  @ViewChild('dropdownContainer', { static: false })
  dropdownContainer!: ElementRef;
  private destroy$ = new Subject<void>();

  constructor(
    private commonService: CommonService,
    private formService: FormsServiceService,
    private toaster: ToastrService,
    private initialsService: InitialsService,
    private router: Router,
    private profilePhotoService: ProfilePhotoService,
    private cdr: ChangeDetectorRef,
  ) {}
  ngOnInit() {
    this.getData();
    this.generatePageNumbers();
    fromEvent(window, 'scroll', { capture: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (this.openDropdownId) {
          this.openDropdownId = null;
          this.openAboveId = null;
          this.cdr.detectChanges();
        }
        if (!this.isFilterDropdownOpen) return;
        const target = event.target as any;
        if (
          !target?.closest?.('.cdk-overlay-container') &&
          !this.dropdownContainer?.nativeElement?.contains(target)
        ) {
          this.closeDropdown();
        }
      });
  }

  /** Function to load the form with the formId */
  loadForm(formId: string, recordId?: any) {
    this.formService.loadFormData(formId, recordId).subscribe({
      next: (data) => {
        this.formData = data.data;
        this.formFields = this.formData?.formFields;
        this.footerFields = this.formData?.footerFields;
        this.buildForm();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading form JSON:', err);
        this.cdr.detectChanges();
      },
    });
  }

  buildForm() {
    const group: { [key: string]: any } = {};
    const dynamicallyControlledFields = new Set<string>();

    // Track dynamically controlled fields
    this.formFields.forEach((field: any) => {
      if (Array.isArray(field.fieldDependencies)) {
        field.fieldDependencies.forEach((dependency: any) => {
          if (
            dependency.trigger === 'onValueChange' &&
            Array.isArray(dependency.condition)
          ) {
            dependency.condition.forEach((cond: any) => {
              if (Array.isArray(cond.actions)) {
                cond.actions.forEach((action: any) => {
                  if (action.targetField) {
                    dynamicallyControlledFields.add(action.targetField);
                  }
                });
              }
            });
          }
        });
      }
    });

    this.formFields.forEach((field: any) => {
      this.visibleFields[field.fieldName] = !dynamicallyControlledFields.has(
        field.fieldName,
      );

      if (
        ['label', 'button', 'grid', 'video', 'noData'].includes(field.fieldType)
      ) {
        return;
      }

      const validators = [];
      const validationRules = Array.isArray(field.validationRules)
        ? field.validationRules
        : [];

      if (
        field.isRequired &&
        !dynamicallyControlledFields.has(field.fieldName)
      ) {
        validators.push(Validators.required);
      }

      const minLength = validationRules.find(
        (v: any) => v.type === 'minLength',
      );
      if (minLength) validators.push(Validators.minLength(minLength.value));

      const maxLength = validationRules.find(
        (v: any) => v.type === 'maxLength',
      );
      if (maxLength) validators.push(Validators.maxLength(maxLength.value));

      const minValue = validationRules.find((v: any) => v.type === 'min');
      if (minValue) validators.push(Validators.min(minValue.value));

      const maxValue = validationRules.find((v: any) => v.type === 'max');
      if (maxValue) validators.push(Validators.max(maxValue.value));

      const regex = validationRules.find(
        (v: any) => v.type?.toLowerCase() === 'regex',
      );
      if (regex?.value)
        validators.push(Validators.pattern(new RegExp(regex.value)));

      if (
        field.fieldType === 'email' ||
        validationRules.some((v: any) => v.type === 'email')
      ) {
        validators.push(Validators.email);
      }

      // Handle other fields
      else {
        const existingValue = this.saveForm?.get(field.fieldName)?.value;

        const defaultValue = existingValue ?? field.value ?? '';
        group[field.fieldName] = new FormControl(defaultValue, validators);
      }
    });

    this.saveForm = new FormGroup(group);
  }
  /** Function to get the data by api or static */
  getData() {
    // Retain filters from sessionStorage on initial load
    const storedFiltersStr = sessionStorage.getItem('usersList');
    if (storedFiltersStr) {
      try {
        this.filterOptions = JSON.parse(storedFiltersStr);
        this.hasActiveDropdownFilters =
          Object.keys(this.filterOptions).length > 0;
      } catch (e) {
        console.error('Error parsing stored usersList filters:', e);
      }
    }

    if (this.fields.options.isStatic === true) {
      this.fieldsData = this.filterRecords(this.fields.options.staticOptions);
      this.loadProfilePhotos();
    } else {
      const formId = this.fields.options.source.formID;

      // Base params
      const params: any = {
        page: 1,
        limit: this.currentLimit,
        query: this.searchInput,
        sort: 'status,name',
        ...this.filterOptions,
      };

      this.getColumns(formId);
    }
  }

  /** Change the page and update current page data */
  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    const formId = this.fields.options.source.formID;
    this.loadData(formId, {
      page: this.currentPage,
      limit: this.currentLimit,
      query: this.searchInput,
      sort: this.sortValue || 'status,name',
      ...this.filterOptions,
    });
  }

  /** Handle page size change (records per page) */
  onPerPageChange(event: any): void {
    const formId = this.fields.options.source.formID;
    this.currentLimit = +event.target.value;
    this.loadData(formId, {
      page: 1,
      limit: this.currentLimit,
      query: this.searchInput,
      sort: this.sortValue || 'status,name',
      ...this.filterOptions,
    });
  }

  /***  Go to the previous page */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.changePage(this.currentPage - 1);
    }
  }

  /** Go to the next page */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.changePage(this.currentPage + 1);
    }
  }

  getInitials(name: string): string {
    return this.initialsService.getInitials(name);
  }

  /** Show color for the user department wise */
  getBgColorByDepartment(name: string): string {
    return getAvatarBgColor(name);
  }

  loadProfilePhotos(): void {
    // No-op since we use profilePhoto directly
  }

  getAssigneePhotoUrl(item: any): string {
    const photoKey = item?.profilePhoto;
    if (photoKey) {
      return `${photoKey}`;
    }
    return './assets/media/icons/default-user-icon.png';
  }

  handleImageError(event: any) {
    event.target.src = './assets/media/icons/default-user-icon.png';
  }

  ngOnDestroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.activeLoadSubscription) {
      this.activeLoadSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
  onKeyUpSearch(searchInput: any) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      if (searchInput.length >= 3 || searchInput.length === 0) {
        this.searchInput = searchInput;
        const formId = this.fields.options.source.formID;
        this.loadData(formId, {
          query: this.searchInput,
          page: 1,
          limit: this.currentLimit,
          sort: this.sortValue || 'status,name',
          ...this.filterOptions,
        });
      }
    }, 300);
  }

  loadData(
    formId: any,
    pagination: { query: any; page?: number; limit?: number; sort?: any } = {
      query: this.searchInput,
      page: 1,
      limit: this.currentLimit,
      sort: 'status,name',
    },
  ) {
    if (this.activeLoadSubscription) {
      this.activeLoadSubscription.unsubscribe();
    }
    this.isLoader = true;
    this.isReloading = true;

    let httpParams = new HttpParams()
      .set('page', pagination.page ? String(pagination.page) : '1')
      .set(
        'limit',
        pagination.limit ? String(pagination.limit) : String(this.currentLimit),
      )
      .set('sort', pagination.sort || 'status,name');

    if (pagination.query) {
      httpParams = httpParams.set('query', pagination.query);
    }

    let hasFilters = this.hasActiveDropdownFilters;

    Object.keys(pagination).forEach((key) => {
      if (!['page', 'limit', 'sort', 'query'].includes(key)) {
        const val = (pagination as any)[key];
        if (val !== undefined && val !== null && val !== '') {
          if (Array.isArray(val)) {
            val.forEach((item: any) => {
              if (item !== undefined && item !== null && item !== '') {
                const subItems =
                  typeof item === 'string' ? item.split(',') : [item];
                subItems.forEach((sub: any) => {
                  const trimmed = typeof sub === 'string' ? sub.trim() : sub;
                  if (trimmed !== '') {
                    httpParams = httpParams.append(key, trimmed);
                  }
                });
              }
            });
          } else if (typeof val === 'string' && val.includes(',')) {
            val.split(',').forEach((sub: string) => {
              const trimmed = sub.trim();
              if (trimmed !== '') {
                httpParams = httpParams.append(key, trimmed);
              }
            });
          } else {
            httpParams = httpParams.append(key, val);
          }
        }
      }
    });

    if (!hasFilters) {
      if (this.useStatusFallback) {
        httpParams = httpParams
          .delete('status')
          .append('status', 'ACTIVE')
          .append('status', 'IN-ACTIVE');
      } else {
        httpParams = httpParams.set(
          'groups',
          '(contactType=Primary&status!=DELETED|SUSPENDED|ARCHIVE&userType!=Guest)|(contactType=Others&invite=SENT|ACCEPTED&userType!=Guest)',
        );
      }
    }

    this.activeLoadSubscription = this.commonService
      .loadListWithOptions(formId, httpParams)
      .subscribe({
        next: (data) => {
          const records = data?.data?.records || [];
          const totalCount = data?.data?.totalCount ?? records.length;

          // If groups query returns 0 records on initial load, fallback to status=ACTIVE&status=IN-ACTIVE
          if (
            httpParams.has('groups') &&
            !this.useStatusFallback &&
            (records.length === 0 || totalCount === 0)
          ) {
            this.useStatusFallback = true;
            this.loadData(formId, pagination);
            return;
          }

          this.isLoader = false;
          this.fieldsData = records;
          this.totalCount = totalCount;
          this.currentPage = data?.data?.page || 1;
          this.totalPages = data?.data?.totalPages || 1;
          this.pageNumbers = Array.from(
            { length: this.totalPages },
            (_, i) => i + 1,
          );
          this.generatePageNumbers();
          this.isReloading = false;
          this.loadProfilePhotos();
          this.cdr.detectChanges();
        },
        error: (err) => {
          if (httpParams.has('groups') && !this.useStatusFallback) {
            this.useStatusFallback = true;
            this.loadData(formId, pagination);
            return;
          }
          this.isLoader = false;
          this.isReloading = false;
          this.fieldsData = [];
          this.cdr.detectChanges();
        },
      });
  }

  getColumns(formId: any) {
    this.formService.loadFormData(formId).subscribe({
      next: (data) => {
        this.columnsData = data.data;
        if (this.columnsData?.formCollection?.columns) {
          const alwaysExclude = ['_id', 'createdDate', 'updatedDate', 'phone'];
          const hasName = this.columnsData.formCollection.columns.some(
            (col: any) => col.fieldName === 'name',
          );
          const hasEmail = this.columnsData.formCollection.columns.some(
            (col: any) => col.fieldName === 'email',
          );
          this.hasNameEmailMerge = hasName && hasEmail;
          this.columns = this.columnsData.formCollection.columns
            .filter((col: any) => {
              if (this.hasNameEmailMerge && col.fieldName === 'email') {
                return false;
              }
              return col.visible && !alwaysExclude.includes(col.fieldName);
            })
            .map((col: any) => col.fieldName);
          this.columns.push('action');
          const params: any = {
            page: 1,
            limit: this.currentLimit,
            query: this.searchInput,
            sort: this.sortValue || 'status,name',
            ...this.filterOptions,
          };
          this.loadData(formId, params);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.columnsData = [];
        this.cdr.detectChanges();
      },
    });
  }
  getColumnDisplayName(fieldName: string): string {
    if (fieldName === 'role.role') return 'Role';
    if (fieldName === 'contactType') return 'Contact Type';
    if (fieldName === 'userType') return 'User Type';

    const col = this.columnsData?.formCollection?.columns.find(
      (c: any) => c.fieldName === fieldName,
    );
    return col && col.displayName && col.displayName.trim() !== ''
      ? col.displayName
      : fieldName;
  }

  isSortable(fieldName: string): boolean {
    const col = this.columnsData?.formCollection?.columns.find(
      (c: any) => c.fieldName === fieldName,
    );
    return col ? col.sortable : false;
  }

  getSkeletonWidth(fieldName: string): number {
    if (fieldName === 'role' || fieldName === 'role.role') return 90;
    if (fieldName === 'contactType') return 100;
    if (fieldName === 'userType') return 80;
    return 110;
  }

  closeOnBackdropClick(event: MouseEvent) {
    const modal = document.getElementById('confirm_modal');
    if (event.target === modal) {
      this.closeModal();
    }
  }

  closeModal() {
    const modal = document.getElementById('confirm_modal');
    if (modal) modal.classList.remove('show');
    this.activeModalContext = null;
  }

  updateUserStatus(item: any, status: string) {
    this.openDropdownId = null;
    this.nodeId = item.id;
    this.activeModalContext = {
      fullName: item.name,
      email: item.email,
      clickedButton: status,
      invite: item.invite,
      status: item.status,
    };
    //  if (status == 'ACTIVE') {
    //   this.activeModalContext = {
    //     fullName: item.name,
    //     email: item.email,
    //     clickedButton: status,
    //     invite: item.invite,
    //     status: item.status,
    //     nodeId: '68edeb0bec54460a09c7d06e',
    //   };
    // }
    if (status == 'ACTIVE') {
      this.activeModalContext = {
        fullName: item.name,
        email: item.email,
        clickedButton: status,
        invite: item.invite,
        status: item.status,
        nodeId: '6a143360ca440a759a89fa08',
        department: item.department,
      };
    } else if (status == 'REINVITE') {
      this.activeModalContext = {
        fullName: item.name,
        email: item.email,
        clickedButton: status,
        invite: item.invite,
        status: item.status,
        nodeId: '69180b3bb3591aac94cdf4bf',
      };
    } else if (status == 'IN-ACTIVE') {
      this.activeModalContext = {
        fullName: item.name,
        email: item.email,
        clickedButton: status,
        invite: item.invite,
        status: item.status,
        nodeId: '68edea4cec54460a09c7d06a',
      };
    } else if (status == 'EDIT') {
      this.activeModalContext = {
        ...item,
        fullName: item.name || item.fullName,
        email: item.email,
        clickedButton: status,
        invite: item.invite,
        status: item.status,
        nodeId: '6a143360ca440a759a89fa08',
        item: item,
      };
    } else if (status == 'ARCHIVE') {
      this.activeModalContext = {
        fullName: item.name,
        email: item.email,
        clickedButton: status,
        invite: item.invite,
        status: item.status,
        nodeId: '68edeb3aec54460a09c7d070',
      };
    } else if (status == 'SUSPEND') {
      this.activeModalContext = {
        fullName: item.name,
        email: item.email,
        clickedButton: status,
        invite: item.invite,
        status: item.status,
        nodeId: '68edeac3ec54460a09c7d06c',
      };
    }
    if (this.activeModalContext.nodeId == '6a143360ca440a759a89fa08') {
      this.openAddModal(item, 'edit');
    } else {
      this.loadForm(this.activeModalContext.nodeId);
      const modal = document.getElementById('confirm_modal');
      if (modal) modal.classList.add('show');
    }
  }

  updateStatus() {
    this.isLoading = true;

    // Always ensure these exist
    const {
      inviteStatus: currentInviteStatus,
      status: currentStatus,
      clickedButton,
    } = this.activeModalContext;

    let inviteStatus = this.activeModalContext.invite;
    let newStatus = currentStatus || ''; // fallback safety

    switch (clickedButton) {
      case 'ACTIVE':
        // Handle invite progression
        if (currentInviteStatus === 'PENDING') inviteStatus = 'SENT';
        else if (currentInviteStatus === 'SENT') inviteStatus = 'ACCEPTED';
        else inviteStatus = '';

        newStatus = 'ACTIVE';
        break;

      case 'ARCHIVE':
        inviteStatus = 'SENT';
        newStatus = 'ARCHIVED';
        break;

      case 'REINVITE':
        // Do not change user status, only resend invite
        inviteStatus = 'SENT';
        newStatus = currentStatus; // keep original
        break;

      case 'SUSPEND':
        inviteStatus = 'ACCEPTED';
        newStatus = 'SUSPENDED';
        break;

      case 'IN-ACTIVE':
        inviteStatus = 'ACCEPTED';
        newStatus = 'IN-ACTIVE';
        break;

      case 'EDIT':
        newStatus = currentStatus;
        break;
    }

    const payload = {
      fullName: this.activeModalContext.fullName,
      email: this.activeModalContext.email,
      invite: inviteStatus,
      status: newStatus,
    };

    this.commonService.updateUserStatus(payload).subscribe({
      next: (res: any) => {
        const successMsg =
          this.formData?.apiIntegration?.apiSuccessMessage || res.message;

        this.toaster.success(successMsg, 'Success', {
          messageClass: 'addMessage',
          titleClass: 'addCss',
        });

        setTimeout(() => {
          const formId = this.fields.options.source.formID;

          this.loadData(formId, {
            page: this.currentPage,
            limit: this.currentLimit,
            query: this.searchInput,
            sort: this.sortValue || 'status,name',
          });

          this.closeModal();
          this.isLoading = false;
        }, 500);
        this.cdr.detectChanges();
      },

      error: (err) => {
        const errorMsg =
          this.formData?.apiIntegration?.apiErrorMessage || err.message;

        this.toaster.error(errorMsg, 'Error', {
          messageClass: 'addMessage',
          titleClass: 'addCss',
        });
        this.cdr.detectChanges();
      },
    });
  }

  // Toggle dropdown for a particular item
  toggleDropdown(id: string, event: MouseEvent) {
    document.dispatchEvent(new CustomEvent('closeAllOverlays'));
    event.stopPropagation(); // prevent event bubbling

    // If already open, close it
    if (this.openDropdownId === id) {
      this.openDropdownId = null;
      this.openAboveId = null;
      return;
    }

    // Open this dropdown
    this.openDropdownId = id;

    // Immediately calculate position using the clicked button
    const target = event.currentTarget as HTMLElement;
    this.calculateDropdownPosition(id, target);

    // Wait for the menu to render before measuring its exact height
    requestAnimationFrame(() => {
      this.calculateDropdownPosition(id);
      this.cdr.detectChanges();
    });
  }

  calculateDropdownPosition(id: string, targetElement?: HTMLElement) {
    const button =
      targetElement ||
      this.dropdownButtons?.find((btn) => btn.nativeElement.dataset.id === id)
        ?.nativeElement;

    const menu = this.dropdownMenus?.find(
      (dropdownMenu) => dropdownMenu.nativeElement.dataset.menu === id,
    );

    if (button) {
      const rect = button.getBoundingClientRect();
      const containerRect =
        this.tableBodyContainer?.nativeElement.getBoundingClientRect();
      const dropdownHeight = menu?.nativeElement.offsetHeight || 190;
      const gap = 12;
      const spaceBelow = containerRect
        ? containerRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const spaceAbove = containerRect
        ? rect.top - containerRect.top
        : rect.top;

      if (spaceBelow < dropdownHeight + gap && spaceAbove > spaceBelow) {
        this.openAboveId = id;
      } else {
        this.openAboveId = null;
      }
      this.cdr.markForCheck();
    }
  }

  // Check if the dropdown for this item is open
  isDropdownOpen(id: string): boolean {
    return this.openDropdownId === id;
  }

  shouldOpenAbove(id: string): boolean {
    return this.openAboveId === id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown')) {
      this.openDropdownId = null;
      this.openAboveId = null;
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.openDropdownId) {
      this.openDropdownId = null;
      this.openAboveId = null;
      this.cdr.detectChanges();
    }
  }

  onTableScroll() {
    if (this.openDropdownId) {
      this.openDropdownId = null;
      this.openAboveId = null;
      this.cdr.detectChanges();
    }
  }

  generatePageNumbers() {
    const total = this.totalPages;
    const current = this.currentPage;
    const visible = 2; // pages visible on each side of current

    let pages: any[] = [];

    // Always show first page
    pages.push(1);

    // Left ellipsis
    if (current > visible + 2) {
      pages.push('...');
    }

    // Middle pages
    const start = Math.max(2, current - visible);
    const end = Math.min(total - 1, current + visible);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Right ellipsis
    if (current < total - (visible + 1)) {
      pages.push('...');
    }

    // Always show last page
    if (total > 1) {
      pages.push(total);
    }

    this.pageNumbers = pages;
  }

  onSort(colKey: string, direction: 'asc' | 'desc') {
    if (this.fieldsData.length > 0) {
      this.currentSort = { [colKey]: direction };
      this.sortValue = direction === 'asc' ? colKey : `-${colKey}`;

      // Base options
      const options: any = {
        page: this.currentPage,
        limit: this.currentLimit,
        query: this.searchInput || '',
        sort: this.sortValue,
      };

      const formId = FORM_ID.USERS;
      this.loadData(formId, {
        query: this.searchInput,
        page: this.currentPage,
        limit: this.currentLimit,
        sort: this.sortValue,
      });
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: any) {
    this.closeModal();
    this.closeAddModal(this.currentOpenModalId);
  }

  toggleSort(fieldName: string) {
    const nextDirection =
      this.currentSort[fieldName] === 'desc' ? 'asc' : 'desc';

    this.onSort(fieldName, nextDirection);
  }

  clearSearch(input: any) {
    input.value = '';
    this.searchInput = '';
    this.onKeyUpSearch(this.searchInput);
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnter(event: any) {
    event.preventDefault();

    if (this.saveForm.valid && !this.isLoading) {
      this.updateStatus();
    }
  }

  reloadData() {
    this.isReloading = true;

    this.commonService.syncUsers().subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.toaster.success(
            res.message || 'Users synced successfully',
            'Success',
            {
              messageClass: 'addMessage',
              titleClass: 'addCss',
            },
          );

          const formId = FORM_ID.USERS;

          this.loadData(formId, {
            page: this.currentPage,
            limit: this.currentLimit,
            query: this.searchInput,
            sort: this.sortValue || 'status,name',
          });
        }
        this.cdr.detectChanges();
      },
      error: (err: Error) => {
        this.handleReloadError(err);
        this.cdr.detectChanges();
      },
    });
  }

  handleReloadError(err: any) {
    this.isReloading = false;
    this.toaster.error(err.message, 'Error', {
      messageClass: 'addMessage',
      titleClass: 'addCss',
    });
    setTimeout(() => {
      this.router.navigate([`${this.endpoint}/google/oauth/start`]);
    }, 200);
  }

  closeOnBackdropClicks(event: MouseEvent) {
    const modalKey = 'showModal_' + this.addModalContext.nodeId;
    const modal = document.getElementById(modalKey);
    if (event.target === modal) {
      this.closeAddModal(modalKey);
    }
  }

  modalStates: { [key: string]: boolean } = {};

  openAddModal(item?: any, type?: string) {
    if (type == 'add') {
      const role = Array.isArray(item?.role)
        ? item.role.length > 0
          ? item.role[0]
          : null
        : item?.role;
      this.addModalContext = {
        ...(item || {}),
        nodeId: FORM_ID.ADD_USER,
        recordID: 'add',
        ...(role !== undefined ? { role } : {}),
      };
    } else if (type == 'edit') {
      const role = Array.isArray(item?.role)
        ? item.role.length > 0
          ? item.role[0]
          : null
        : item?.role;
      this.addModalContext = {
        ...(item || {}),
        nodeId: FORM_ID.INVITE_USER,
        recordID: item?._id,
        clickedButton: this.activeModalContext?.clickedButton || 'EDIT',
        type: 'edit',
        item: item,
        ...(role !== undefined ? { role } : {}),
      };
    } else if (type == 'invite') {
      this.addModalContext = {
        nodeId: FORM_ID.ACTIVATE_AND_INVITE,
        recordID: 'invite',
      };
    }

    const modalKey = 'showModal_' + this.addModalContext.nodeId;
    this.currentOpenModalId = modalKey;
    this.modalStates[modalKey] = true;
  }

  closeAddModal(nodeId: string) {
    const modalKey = 'showModal_' + nodeId;
    this.modalStates[modalKey] = false;
  }

  isModalOpen(nodeId: string): boolean {
    return !!this.modalStates['showModal_' + nodeId];
  }

  onFormSaved(event: any) {
    const formId = event?.formId || this.addModalContext?.nodeId;
    if (formId === '6a143360ca440a759a89fa08') {
      this.loadData(FORM_ID.USERS, {
        query: this.searchInput,
        page: 1,
        limit: this.currentLimit,
        sort: 'status,name',
      });
    } else if (
      formId === FORM_ID.ADD_USER ||
      formId === '6a143253ca440a759a89f9ff' ||
      formId === FORM_ID.USERS ||
      formId === '6878d3800453556c08933e90'
    ) {
      this.getData();
    }
  }
  getCellValue(item: any, fieldName: string): any {
    if (!fieldName) {
      return '-';
    }

    // Support nested paths like 'role.role'
    let value: any;
    if (fieldName.includes('.')) {
      const keys = fieldName.split('.');
      let current = item;
      for (const key of keys) {
        if (current === null || current === undefined) {
          current = undefined;
          break;
        }
        if (Array.isArray(current)) {
          // If current is an array, try to find a key inside the array items
          // Or collect all values. Let's first look for the key in elements
          const mapped = current
            .map((itemInArray) => itemInArray?.[key])
            .filter((val) => val !== undefined && val !== null);
          current = mapped.length > 0 ? mapped : undefined;
        } else {
          current = current[key];
        }
      }
      value = current;
    } else {
      value = item[fieldName];
    }

    if (value === null || value === undefined || value === '') {
      return '-';
    }

    // Special handling for role
    if (fieldName === 'role') {
      return typeof value === 'object'
        ? value.role || value.name || '-'
        : value;
    }

    // Array of objects or values
    if (Array.isArray(value)) {
      return value.map((v) => v?.name || v?.role || v).join(', ') || '-';
    }

    // Single object
    if (typeof value === 'object') {
      return value.name || value.role || '-';
    }

    // Primitive value
    return value;
  }

  toggleFilterDropdown(): void {
    this.isFilterDropdownOpen = !this.isFilterDropdownOpen;
    if (this.isFilterDropdownOpen) {
      this.activeModalContext = {
        nodeId: FORM_ID.USERS_FILTER,
        targetFormId: this.fields?.options?.source?.formID,
        status: this.filterOptions['status'],
        taskStatus: this.filterOptions['status'],
        ...this.filterOptions,
      };
    }
  }

  /**
   * Closes the filter dropdown
   */
  closeDropdown(): void {
    this.isFilterDropdownOpen = false;
    this.cdr.detectChanges();
  }

  /**
   * Handles filter application:
   * - Sets active filters and calls loadData with options
   */
  onFilterApplied(event: any): void {
    const { formId, options = {} } = event;
    this.filterOptions = options;

    // Clean empty options: remove null/undefined/empty string/empty arrays
    const cleanedOptions: Record<string, any> = {};
    Object.entries(options || {}).forEach(([key, value]) => {
      const isEmpty =
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);
      if (!isEmpty) {
        cleanedOptions[key] = value;
      }
    });

    this.hasActiveDropdownFilters = Object.keys(cleanedOptions).length > 0;

    // Save cleaned filter options in sessionStorage under 'usersList'
    sessionStorage.setItem('usersList', JSON.stringify(cleanedOptions));

    const payload: any = {
      page: 1,
      limit: this.currentLimit,
      query: this.searchInput || '',
      ...cleanedOptions,
    };

    if (!this.hasActiveDropdownFilters) {
      payload.sort = 'status,name';
    }

    const targetFormId = formId || this.fields?.options?.source?.formID;
    this.loadData(targetFormId, payload);
    this.closeDropdown();
  }

  /**
   * Clears all filters and reloads table records
   */
  clearAll(event: any): void {
    const { formId } = event;
    this.filterOptions = {};
    this.hasActiveDropdownFilters = false;

    // Remove filter options from sessionStorage
    sessionStorage.removeItem('usersList');

    // Attempt to reset controls inside modal-renderer if available
    if (
      this.modalRendererRef &&
      typeof this.modalRendererRef.resetFilterFormControl === 'function'
    ) {
      // Just in case we need to trigger reset
    }

    const targetFormId = formId || this.fields?.options?.source?.formID;
    this.loadData(targetFormId, {
      page: 1,
      limit: this.currentLimit,
      query: this.searchInput || '',
      sort: 'status,name',
    });
    this.closeDropdown();
  }

  /**
   * Sets the modal title if needed
   */
  setModalTitle(event: any): void {
    this.modalText = event;
  }

  shouldShowNA(item: any): boolean {
    if (!item) return true;

    if (item.status === 'DELETED') return true;

    const userType = (item.userType || '').toLowerCase();
    if (userType === 'guest') return true;

    // const contactType = (item.contactType || '').toLowerCase();
    // const status = (item.status || '').toLowerCase();
    // const invite = (item.invite || item.inviteStatus || '').toLowerCase();

    // const isPrimaryActiveOrInactive =
    //   contactType === 'primary' &&
    //   (status === 'active' || status === 'in-active');

    // const isOthersSentOrAccepted =
    //   contactType === 'others' && (invite === 'sent' || invite === 'accepted');

    // if (!isPrimaryActiveOrInactive && !isOthersSentOrAccepted) {
    //   return true;
    // }

    return false;
  }

  filterRecords(records: any[]): any[] {
    if (!records) return [];
    return records.filter((item: any) => {
      const contactType = (item.contactType || '').toLowerCase();
      const status = (item.status || '').toLowerCase();
      const invite = (item.invite || item.inviteStatus || '').toLowerCase();

      const isPrimaryActiveOrInactive =
        contactType === 'primary' &&
        (status === 'active' ||
          status === 'inactive' ||
          status === 'in-active');

      const isOthersSentOrAccepted =
        contactType === 'others' &&
        (invite === 'sent' || invite === 'accepted');

      return isPrimaryActiveOrInactive || isOthersSentOrAccepted;
    });
  }
}
